import { getAdminToken } from "@/lib/adminToken";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Save,
  Image as ImageIcon,
  CheckCircle2,
  MapPin,
  Building2,
  Loader2,
  X,
  Plus,
  Upload,
  Trash2,
  Star,
} from "lucide-react";
import TiptapEditor from "@/components/ui/TiptapEditor";

const API_BASE = "/api";
const categories = [
  "As-Built Surveying",
  "Cadastral Surveying",
  "Digital Mapping",
  "Engineering Surveying",
  "Geographic Information System (GIS)",
  "Hydrographic Surveying",
  "Topographic Surveying",
];

interface ProjectImage {
  id: string;
  url: string;
  isFeature: boolean;
}

interface GalleryUploadProgress {
  active: boolean;
  completed: number;
  total: number;
  progress: number;
}

const currentYear = new Date().getFullYear().toString();

function parseProjectYear(year: string | undefined) {
  const fallback = { yearFrom: currentYear, yearTo: currentYear };
  if (!year) return fallback;

  const parts = year
    .split(/\s*(?:-|to)\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return { yearFrom: parts[0], yearTo: parts[1] };
  }

  return { yearFrom: year.trim(), yearTo: year.trim() };
}

function formatProjectYear(yearFrom: string, yearTo: string) {
  const from = yearFrom.trim();
  const to = yearTo.trim();

  if (!to || from === to) return from;
  return `${from} - ${to}`;
}

function CircularProgress({ progress, complete = false }: { progress: number; complete?: boolean }) {
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.min(100, Math.max(0, progress));
  const offset = circumference - (clampedProgress / 100) * circumference;

  return (
    <div className="relative h-10 w-10 flex-shrink-0">
      <svg className="h-10 w-10 -rotate-90" viewBox="0 0 40 40" aria-hidden="true">
        <circle
          cx="20"
          cy="20"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          className="text-white/30"
        />
        <circle
          cx="20"
          cy="20"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={complete ? "text-green-400" : "text-accent"}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {complete ? (
          <CheckCircle2 className="h-4 w-4 text-green-400" />
        ) : (
          <span className="text-[9px] font-semibold text-white">{Math.round(clampedProgress)}%</span>
        )}
      </div>
    </div>
  );
}

export default function ProjectFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const featureImageInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [project, setProject] = useState({
    title: "",
    category: categories[0],
    location: "",
    yearFrom: currentYear,
    yearTo: currentYear,
    client: "",
    description: "",
    status: "Completed",
    services: [] as string[],
  });

  const [featureImage, setFeatureImage] = useState<ProjectImage | null>(null);
  const [galleryImages, setGalleryImages] = useState<ProjectImage[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [newService, setNewService] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [featureUploadProgress, setFeatureUploadProgress] = useState<number | null>(null);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState<GalleryUploadProgress | null>(null);

  const isPreparingImages =
    (featureUploadProgress !== null && featureUploadProgress < 100) ||
    Boolean(galleryUploadProgress?.active);
  const canSave =
    Boolean(project.title && project.location && project.yearFrom && project.client) &&
    !saving &&
    !isPreparingImages;

  useEffect(() => {
    if (isEdit && id) {
      fetchProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/projects/${id}`, { headers: { Authorization: `Bearer ${getAdminToken()}` } });
      if (!response.ok) throw new Error("Project not found");
      const data = await response.json();
      if (data.services && typeof data.services === "string") {
        data.services = JSON.parse(data.services);
      }
      const { yearFrom, yearTo } = parseProjectYear(data.year);
      setProject({
        title: data.title || "",
        category: data.category || categories[0],
        location: data.location || "",
        yearFrom,
        yearTo,
        client: data.client || "",
        description: data.description || "",
        status: data.status || "Completed",
        services: data.services || [],
      });
      if (data.image_url) {
        setFeatureImage({
          id: "feature-1",
          url: data.image_url,
          isFeature: true,
        });
      }
      if (data.gallery_images) {
        const gallery = typeof data.gallery_images === "string" 
          ? JSON.parse(data.gallery_images) 
          : data.gallery_images;
        setGalleryImages(gallery.map((url: string, idx: number) => ({
          id: `gallery-${idx}`,
          url,
          isFeature: false,
        })));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string | string[]) => {
    setProject({ ...project, [field]: value });
  };

  const handleAddService = () => {
    if (newService.trim() && !project.services.includes(newService.trim())) {
      handleChange("services", [...project.services, newService.trim()]);
      setNewService("");
    }
  };

  const handleRemoveService = (serviceToRemove: string) => {
    handleChange(
      "services",
      project.services.filter((s) => s !== serviceToRemove)
    );
  };

  const handleFeatureImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      setFeatureUploadProgress(0);
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          setFeatureUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setFeatureImage({
          id: `feature-${Date.now()}`,
          url: base64,
          isFeature: true,
        });
        setFeatureUploadProgress(100);
      };
      reader.onerror = () => {
        setFeatureUploadProgress(null);
        alert("Feature image could not be prepared. Please try another image.");
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const handleGalleryUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const progressByFile = Array(files.length).fill(0);
    let completed = 0;

    const updateGalleryProgress = () => {
      const totalProgress = progressByFile.reduce((sum, value) => sum + value, 0);
      setGalleryUploadProgress({
        active: completed < files.length,
        completed,
        total: files.length,
        progress: Math.round(totalProgress / files.length),
      });
    };

    setGalleryUploadProgress({
      active: true,
      completed: 0,
      total: files.length,
      progress: 0,
    });

    files.forEach((file, fileIndex) => {
      const reader = new FileReader();
      reader.onprogress = (event) => {
        if (event.lengthComputable) {
          progressByFile[fileIndex] = Math.round((event.loaded / event.total) * 100);
          updateGalleryProgress();
        }
      };
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        setGalleryImages((prev) => [
          ...prev,
          {
            id: `gallery-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            url: base64,
            isFeature: false,
          },
        ]);
        progressByFile[fileIndex] = 100;
        completed += 1;
        updateGalleryProgress();
      };
      reader.onerror = () => {
        progressByFile[fileIndex] = 100;
        completed += 1;
        updateGalleryProgress();
        alert(`Gallery image "${file.name}" could not be prepared. Please try another image.`);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeFeatureImage = () => {
    setFeatureImage(null);
  };

  const removeGalleryImage = (id: string) => {
    setGalleryImages((prev) => prev.filter((img) => img.id !== id));
  };

  const setAsFeature = (id: string) => {
    const image = galleryImages.find((img) => img.id === id);
    if (image) {
      if (featureImage) {
        setGalleryImages((prev) => [...prev, { ...featureImage, isFeature: false }]);
      }
      setFeatureImage({ ...image, isFeature: true });
      setGalleryImages((prev) => prev.filter((img) => img.id !== id));
    }
  };

  const handleSave = async (statusOverride?: string) => {
    if (!project.title || !project.location || !project.yearFrom || !project.client) return;

    setSaving(true);
    try {
      const payload = {
        ...project,
        status: statusOverride || project.status,
        year: formatProjectYear(project.yearFrom, project.yearTo),
        image_url: featureImage?.url || "",
        gallery_images: galleryImages.map((img) => img.url),
      };
      delete (payload as Partial<typeof project> & { year?: string }).yearFrom;
      delete (payload as Partial<typeof project> & { year?: string }).yearTo;

      const url = isEdit ? `${API_BASE}/projects/${id}` : `${API_BASE}/projects`;
      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAdminToken()}` },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.error || "Failed to save project");
      }

      setShowSaved(true);
      setTimeout(() => {
        setShowSaved(false);
        navigate("/admin/projects");
      }, 1500);
    } catch (err) {
      alert("Error saving project: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-accent animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/projects")}
            className="p-2 hover:bg-neutral-100 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-8 h-0.5 bg-accent rounded-full" />
              <span className="text-accent text-xs font-medium uppercase tracking-wide">Projects</span>
            </div>
            <h1 className="text-2xl font-bold text-primary">{isEdit ? "Edit Project" : "Create New Project"}</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin/projects")}
            className="px-4 py-2.5 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSave("Draft")}
            disabled={!canSave}
            className="px-4 py-2.5 rounded-xl border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Draft
          </button>
          <button
            onClick={() => handleSave()}
            disabled={!canSave}
            className="px-4 py-2.5 rounded-xl bg-accent hover:bg-accent/90 text-white transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Save Changes" : "Create Project"}
          </button>
        </div>
      </div>

      {/* Saved Notification */}
      <AnimatePresence>
        {showSaved && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-center gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <span className="text-green-600 text-sm">Project saved successfully!</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <div className="bg-white rounded-xl border border-neutral-100 p-5">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-5">
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Project Title *
              </label>
              <input
                type="text"
                value={project.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                placeholder="Enter project title"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                  Category
                </label>
                <select
                  value={project.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                  Project Years
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={project.yearFrom}
                    onChange={(e) => handleChange("yearFrom", e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="From"
                    aria-label="From year"
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={project.yearTo}
                    onChange={(e) => handleChange("yearTo", e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    placeholder="To"
                    aria-label="To year"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  value={project.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                  placeholder="Project location"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Client
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  value={project.client}
                  onChange={(e) => handleChange("client", e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                  placeholder="Client name"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Status
              </label>
              <select
                value={project.status}
                onChange={(e) => handleChange("status", e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
              >
                <option value="Completed">Completed</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
                <option value="Draft">Draft</option>
              </select>
            </div>

            {/* Services */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Services Provided
              </label>

              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddService();
                    }
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                  placeholder="Add a service..."
                />
                <button
                  type="button"
                  onClick={handleAddService}
                  className="px-4 py-2.5 bg-neutral-100 hover:bg-neutral-200 rounded-xl transition-colors"
                >
                  <Plus className="w-4 h-4 text-neutral-600" />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {project.services.map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent/10 text-accent text-sm rounded-full"
                  >
                    {service}
                    <button
                      type="button"
                      onClick={() => handleRemoveService(service)}
                      className="hover:bg-accent/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>

              {project.services.length === 0 && (
                <p className="text-xs text-neutral-400 mt-2">No services added yet.</p>
              )}
            </div>
          </div>

          {/* Right Column - Images & Description */}
          <div className="space-y-5">
            {/* Feature Image */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Feature Image *
              </label>
              <input
                type="file"
                ref={featureImageInputRef}
                onChange={handleFeatureImageUpload}
                accept="image/*"
                className="hidden"
              />
              
              {featureImage ? (
                <div className="relative rounded-lg overflow-hidden">
                  <img
                    src={featureImage.url}
                    alt="Feature"
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => featureImageInputRef.current?.click()}
                      className="p-2 bg-white text-primary rounded-lg hover:bg-accent hover:text-white transition-colors"
                      title="Change feature image"
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={removeFeatureImage}
                      className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-1 text-white text-xs font-medium">
                        <Star className="w-3 h-3 fill-current" />
                        Feature Image
                      </span>
                      {featureUploadProgress !== null && (
                        <div className="flex items-center gap-2 text-xs font-medium text-white">
                          <span>{featureUploadProgress === 100 ? "Ready to save" : "Preparing"}</span>
                          <CircularProgress
                            progress={featureUploadProgress}
                            complete={featureUploadProgress === 100}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => featureImageInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-neutral-200 rounded-lg flex flex-col items-center justify-center gap-2 text-neutral-400 hover:border-accent hover:text-accent transition-colors"
                >
                  <Upload className="w-8 h-8" />
                  <span className="text-sm">Click to upload feature image</span>
                </button>
              )}
              {featureUploadProgress !== null && featureUploadProgress < 100 && (
                <p className="text-xs text-neutral-400 mt-2">Preparing feature image. You can save after it reaches 100%.</p>
              )}
            </div>

            {/* Gallery Images */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-neutral-500 uppercase tracking-wide">
                  Project Gallery
                </label>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="text-xs text-accent hover:text-accent-dark font-medium flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add Images
                </button>
              </div>
              {galleryUploadProgress && (
                <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-neutral-100 bg-neutral-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-primary">
                      {galleryUploadProgress.active ? "Preparing gallery images" : "Gallery images ready"}
                    </p>
                    <p className="text-xs text-neutral-400">
                      {galleryUploadProgress.completed} of {galleryUploadProgress.total} images ready
                    </p>
                  </div>
                  <CircularProgress
                    progress={galleryUploadProgress.progress}
                    complete={!galleryUploadProgress.active}
                  />
                </div>
              )}
              <input
                type="file"
                ref={galleryInputRef}
                onChange={handleGalleryUpload}
                accept="image/*"
                multiple
                className="hidden"
              />

              {galleryImages.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {galleryImages.map((image) => (
                    <div key={image.id} className="relative aspect-square rounded-lg overflow-hidden group">
                      <img
                        src={image.url}
                        alt="Gallery"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setAsFeature(image.id)}
                          title="Set as feature"
                          className="p-2 bg-white rounded-lg hover:bg-accent hover:text-white transition-colors"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(image.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-24 border-2 border-dashed border-neutral-200 rounded-lg flex items-center justify-center text-neutral-400 text-sm">
                  No gallery images yet
                </div>
              )}
            </div>

            {/* Description with Tiptap */}
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5 uppercase tracking-wide">
                Project Description
              </label>
              <TiptapEditor
                content={project.description}
                onChange={(value) => handleChange("description", value)}
                placeholder="Describe the project..."
              />
              <p className="text-xs text-neutral-400 mt-2">
                Use the toolbar to format text, add headings, lists, links, and images.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
